<script setup>
import { useRouter } from "vue-router/composables";
import { onMounted, onUnmounted, ref } from "vue";
import {
  setSelectedApp,
  setXentralConfig
} from "@/composables/useSalesChannelManagementApp";
import Auth from "@/components/General/Auth/auth";
import { Error } from "@/core/plugins/swal";
import i18n from "@/core/plugins/vue-i18n";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import Progressbar from "@/components/Tools/Progressbar.vue";
import Loader from "@/components/Tools/Loader.vue";
import { useAuthentication } from "../composables/useAuthentication";
import { useLocale } from "@/components/ExternalApps/composables/useLocale";
import { useStore } from "@/core/services/store";
import { useTheme } from "@/composables/useTheme";

const router = useRouter();
const { setLanguage } = useLocale();

const error = ref(false);
const { requestAccessToken } = useAuthentication(login, {
  onTokenRefreshed: accessToken => Auth.renewAppSession(accessToken),
  onLoginFailed: showLoginFailed
});

const store = useStore();

onMounted(() => {
  window.addEventListener("message", handleConfig);
  getConfig();
});

onUnmounted(() => {
  window.removeEventListener("message", handleConfig);
});

function getConfig() {
  error.value = false;
  window.top?.postMessage({ action: "getConfig" }, "*");
}

function login(accessToken) {
  const appName = store?.getters?.selectedApp?.name;

  if (!appName) {
    return;
  }

  Auth.appLogin(accessToken).then(response => {
    if (response.success) {
      removeEventFromLoadingQueue({
        key: "appLogin"
      });
      router.push({
        name: "salesChannelManagementApp",
        params: { name: appName }
      });
    } else {
      removeEventFromLoadingQueue({
        key: "appLogin"
      });
      error.value = true;
      Error(response.data.message);
    }
  });
}

async function handleConfig(event) {
  if (event.data.action === "getConfig") {
    addEventToLoadingQueue({
      key: "appLogin"
    });

    let xentralData = event.data;
    // The id token only carries the host's claims (license id); the login uses the exchanged
    // access token.
    const idToken = xentralData?.data?.idToken;
    if (!idToken) {
      showLoginFailed();
      return;
    }
    xentralData.data.idToken = parseJwt(idToken);
    await setXentralConfig(xentralData);

    let appName = event.data?.data?.id.replace("connect-", "");
    await setSelectedApp(appName);

    if (xentralData?.data?.themeMode) {
      const { applyTheme } = useTheme();
      applyTheme(xentralData.data.themeMode);
    }

    if (process.env.VUE_APP_ENABLE_APP_MULTI_LANGUAGE === "true") {
      await setLanguage();
    }

    requestAccessToken();
  }
}

function showLoginFailed() {
  removeEventFromLoadingQueue({
    key: "appLogin"
  });
  error.value = true;
  Error(i18n.t("salesChannelManagementApp.authFailed"));
}

function parseJwt(token) {
  let base64Url = token.split(".")[1];
  let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  let jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );
  return JSON.parse(jsonPayload);
}
</script>

<template>
  <div>
    <Progressbar />

    <div class="apps-auth-panel">
      <Loader v-if="!error" />

      <button v-else class="btn btn-primary" @click="getConfig">
        {{ $t("salesChannelManagementApp.authRetry") }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.apps-auth-panel {
  height: 100%;
  width: 100%;
  display: grid;
  place-content: center;
  background-color: var(--ax-bg-body, #{$color-connect-background});

  .btn {
    font-size: 2.5rem;
    padding: 20px 60px;
    width: 450px;

    i {
      padding: 0;
      font-size: 2.5rem;
    }
  }
}
</style>
